import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "util/context/user_context";
import {
  getNotificationsApi,
  checkAllNotificationsApi,
} from "api/notification";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  amber50: "#FFFBEB",
  amber600: "#D97706",
  red: "#EF4444",
};

function relativeTime(s) {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return s.slice(5, 10).replace("-", "/");
}

// 알림 클릭 시 이동할 경로 결정
export function notificationTarget(notif, userType) {
  if (!notif) return "/";
  // 수리(매칭) 알림
  if (notif.type === "수리" && notif.matchDocId) {
    return userType === "수리기사"
      ? `/received/${notif.matchDocId}`
      : `/matching/${notif.matchDocId}`;
  }
  // 내 글에 댓글이 달림 — matchDocId 필드에 community docId 가 저장돼 있음
  if (notif.type === "댓글" && notif.matchDocId) {
    return `/community/${notif.matchDocId}`;
  }
  // 작성(커뮤니티 새 글) — 더 이상 발송 안 함. 옛 데이터에 한해 목록으로.
  if (notif.type === "작성") return "/community";
  return "/";
}

// 표시에서 제외할 타입 (작성 = 더 이상 생성 안 됨, 옛 데이터)
export const HIDDEN_NOTIF_TYPES = new Set(["작성"]);

function NotificationRow({ notif, onClick }) {
  const isMatch = notif.type === "수리";
  const isComment = notif.type === "댓글";
  const isCommunity = notif.type === "작성";
  let categoryName = "";
  try {
    const arr = JSON.parse(notif.categoryListName || "[]");
    categoryName = Array.isArray(arr) ? arr[0] || "" : "";
  } catch {}
  const unread = !notif.checkDate || notif.checkDate === "";

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "12px 14px",
        border: "none",
        background: unread ? C.greenBg : "#fff",
        borderBottom: `1px solid ${C.line}`,
        cursor: "pointer",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = unread ? "#DCFCE7" : "#F9FAFB";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = unread ? C.greenBg : "#fff";
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 9999,
          background: isMatch ? "#FFF7ED" : isComment ? C.greenBg : "#EFF6FF",
          color: isMatch ? "#C2410C" : isComment ? C.greenDark : "#1D4ED8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 16,
        }}
        aria-hidden
      >
        {isMatch ? "🔧" : isComment ? "💬" : isCommunity ? "📝" : "🔔"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: "-0.01em",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {isMatch
            ? `${categoryName || "새 수리요청"} 알림`
            : isComment
            ? `${categoryName || "새"} 댓글`
            : isCommunity
            ? "커뮤니티 새 글"
            : "알림"}
          {unread && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 9999,
                background: C.red,
              }}
              aria-label="읽지 않음"
            />
          )}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 12,
            color: C.body,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {isMatch
            ? notif.address || "수리 요청이 도착했어요"
            : isComment
            ? `${notif.communityTitle ? `[${notif.communityTitle}] ` : ""}${notif.address || "댓글이 달렸어요"}`
            : isCommunity
            ? notif.communityTitle || "새 글이 올라왔어요"
            : ""}
        </div>
        <div style={{ marginTop: 4, fontSize: 11, color: C.sub }}>
          {relativeTime(notif.createDate)}
        </div>
      </div>
    </button>
  );
}

export default function NotificationBell({ size = 22 }) {
  const { userData } = useUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const userId = userData?.id;
  const userType = userData?.userType;

  const refresh = async () => {
    if (!userId) return;
    setLoading(true);
    const data = await getNotificationsApi(userId);
    setList(data);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // 더 이상 사용하지 않는 type='작성'(커뮤니티 새 글) 은 옛 데이터라 숨김
  const visible = useMemo(
    () => list.filter((n) => !HIDDEN_NOTIF_TYPES.has(n.type)),
    [list],
  );
  const unreadCount = visible.filter(
    (n) => !n.checkDate || n.checkDate === "",
  ).length;

  const handleOpen = async () => {
    setOpen((p) => !p);
    if (!open && unreadCount > 0) {
      // 드롭다운 펼치면 일괄 읽음 처리
      await checkAllNotificationsApi(userId);
      // 리스트 자체 데이터에서도 checkDate 채워서 시각적 일관성 유지
      setList((prev) =>
        prev.map((n) =>
          !n.checkDate || n.checkDate === ""
            ? { ...n, checkDate: new Date().toISOString().slice(0, 16) }
            : n,
        ),
      );
    }
  };

  const handleSelect = (notif) => {
    setOpen(false);
    navigate(notificationTarget(notif, userType));
  };

  const top = visible.slice(0, 5);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={handleOpen}
        aria-label="알림"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 6,
          borderRadius: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          lineHeight: 0, // SVG 가 인라인으로 누르는 여백 제거
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="#374151"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 9999,
              background: C.red,
              color: "#fff",
              fontSize: 10,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid #fff",
              lineHeight: 1,
            }}
            aria-label={`읽지 않은 알림 ${unreadCount}개`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 360,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "70vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
            border: `1px solid ${C.line}`,
            borderRadius: 14,
            boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
            zIndex: 200,
            lineHeight: 1.4,
            fontFamily:
              'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${C.line}`,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>
              알림
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
              style={{
                background: "none",
                border: "none",
                color: C.greenDark,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              전체 보기 →
            </button>
          </div>

          <div style={{ overflowY: "auto", maxHeight: "56vh" }}>
            {loading ? (
              <div
                style={{
                  padding: "30px 16px",
                  color: C.sub,
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                불러오는 중...
              </div>
            ) : top.length === 0 ? (
              <div
                style={{
                  padding: "30px 16px",
                  color: C.sub,
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                새 알림이 없어요
              </div>
            ) : (
              top.map((n) => (
                <NotificationRow
                  key={n.docId}
                  notif={n}
                  onClick={() => handleSelect(n)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
